import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function TalentPoolVerify() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("");
  const [setPasswordToken, setSetPasswordToken] = useState("");
  const [stageOnePassed, setStageOnePassed] = useState(null);
  const [hardBlockerMessages, setHardBlockerMessages] = useState([]);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (!token) {
      setStatus("error");
      setMessage("Missing token. Please use the link from your email.");
      return;
    }

    api
      .post("/talent-pool/verify-email", { token })
      .then((r) => {
        setSetPasswordToken(r.data.set_password_token || "");
        setStageOnePassed(Boolean(r.data.stage_one_passed));
        setHardBlockerMessages(r.data.hard_blocker_messages || []);
        setStatus("success");
      })
      .catch((err) => {
        setStatus("error");
        setMessage(err.response?.data?.detail || "Could not verify this link.");
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center bg-card border border-border rounded-lg p-10">
        <Link to="/" className="text-xs uppercase tracking-[0.2em] text-ink font-semibold">
          ← Railway Careers UK
        </Link>

        {status === "verifying" && (
          <>
            <Loader2 className="w-12 h-12 text-amber-600 mx-auto mt-6 animate-spin" />
            <h1 className="font-display font-extrabold text-ink text-2xl mt-5">Verifying your email…</h1>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-lime-600 mx-auto mt-6" data-testid="tp-verify-success" />
            <h1 className="font-display font-extrabold text-ink text-2xl mt-5">Email verified</h1>
            {stageOnePassed ? (
              <>
                <p className="mt-3 text-sm text-muted-foreground">
                  Stage One mandatory checks are met. Next step: choose your password to access your dashboard.
                </p>
                <div className="mt-4 rounded-md border border-sky/30 bg-sky/10 p-3 text-sm text-ink">
                  Stage 2 target role selection is coming soon.
                </div>
              </>
            ) : (
              <>
                <p className="mt-3 text-sm text-muted-foreground">
                  Your Stage One responses currently do not meet mandatory progression checks, so you cannot progress to Stage 2 at this time.
                </p>
                {hardBlockerMessages.length > 0 && (
                  <ul className="mt-3 text-left text-sm text-ink space-y-1">
                    {hardBlockerMessages.map((msg) => (
                      <li key={msg}>• {msg}</li>
                    ))}
                  </ul>
                )}
                <div className="mt-4 rounded-md border border-amber/40 bg-amber/10 p-3 text-sm text-ink">
                  You can still set your password to access your dashboard and receive updates.
                </div>
              </>
            )}
            <Link
              to={`/talent-pool/set-password?token=${encodeURIComponent(setPasswordToken)}`}
              className="inline-block mt-6 px-5 py-2.5 rounded-full bg-navy text-white hover:bg-lime-600 hover:text-slate-950 font-semibold transition-colors"
              data-testid="tp-verify-next"
            >
              Choose password
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="w-12 h-12 text-red-500 mx-auto mt-6" data-testid="tp-verify-error" />
            <h1 className="font-display font-extrabold text-ink text-2xl mt-5">Verification failed</h1>
            <p className="mt-3 text-sm text-muted-foreground">{message}</p>
            <Link
              to="/talent-pool/register"
              className="inline-block mt-6 px-5 py-2.5 rounded-full bg-navy text-white hover:bg-lime-600 hover:text-slate-950 font-semibold transition-colors"
            >
              Register again
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
